/**
 * Thin axios wrappers for candidate self-service volunteer delegation
 * (outreach MVP). These back the "My Volunteers" view, where a CANDIDATE user
 * provisions field volunteers bounded to a subset of their own turf.
 *
 * Two endpoints, both enforced server-side:
 *   GET  /candidates/mine                 — the candidates the caller is linked
 *                                           to (as candidate or staff), each
 *                                           with its authoritative reach.
 *   POST /candidates/{id}/staff           — create a scoped volunteer account.
 *
 * The scope a volunteer is granted can never EXCEED the candidate's reach; the
 * backend rejects an over-broad grant with 403. The UI mirrors this by only
 * ever OFFERING in-reach options, but the server is the source of truth.
 *
 * Axios errors are intentionally left to propagate — call sites read
 * `err.response?.data?.detail` to surface the server's message (403 scope /
 * ownership, 422 bad role/email, 409 email exists). Pattern mirrors
 * `src/api/candidates.ts`.
 */
import client from '@/api/client';
import type { CandidateScopeKind } from '@/types/candidate';

// ---------------------------------------------------------------------------
// Read shapes
// ---------------------------------------------------------------------------

/**
 * One reach row on a candidate the caller is linked to. Identical wire shape to
 * `CandidateScopeRead` (`{kind, ld, precincts, label}`) — the `label` is
 * precomputed server-side ("LD 12" / "LD 12 — precincts 8, 35").
 */
export interface CandidateReachScope {
  kind: CandidateScopeKind;
  ld: string;
  /** Present (non-null) for 'subregion'; null for a whole-LD scope. */
  precincts: string[] | null;
  label: string;
}

/** How the caller relates to a candidate row. */
export type CandidateRelation = 'candidate' | 'staff';

/**
 * One row of `GET /candidates/mine`: a candidate the caller is linked to,
 * plus whether the caller may delegate (only a candidate with `can_delegate`
 * can add volunteers) and the candidate's authoritative reach.
 */
export interface MyCandidate {
  candidate_id: number;
  slug: string;
  full_name: string;
  office: string;
  district_label: string | null;
  relation: CandidateRelation;
  can_delegate: boolean;
  /** The CANDIDATE's whole authoritative reach — what they may grant FROM. */
  scopes: CandidateReachScope[];
  /**
   * The caller's OWN turf that this candidate granted them — a slice of
   * `scopes`, not the same fact. A volunteer holding two precincts of a
   * district must not be shown the district; that reads as access they do not
   * have.
   *
   * Empty for a candidate's own link (their turf IS the reach), for
   * admin-granted turf, and for grants made before the server started
   * recording a grantor. Empty means "nothing attributed here", never "no
   * access" — fall back to `scopes` in the copy, don't render a zero.
   */
  my_scopes: CandidateReachScope[];
}

// ---------------------------------------------------------------------------
// Write shapes
// ---------------------------------------------------------------------------

/** The role a delegated volunteer can be given. */
export type DelegatedRole = 'canvasser' | 'viewer';

/**
 * One scope row in a delegation grant. Wire spelling matches
 * `CandidateScopeInput` (`{kind, ld, precincts}`), NOT the DB column spelling.
 * `precincts` is required for 'subregion' and omitted for 'ld'; strings on the
 * wire, never numbers.
 */
export interface DelegateScopeInput {
  kind: CandidateScopeKind;
  ld: string;
  precincts?: string[];
}

/** Body for `POST /candidates/{id}/staff`. */
export interface DelegateStaffPayload {
  email: string;
  /** Temporary password, min 8 chars; the volunteer can change it later. */
  password: string;
  full_name?: string;
  role: DelegatedRole;
  scopes: DelegateScopeInput[];
}

/**
 * Response from a successful `POST /candidates/{id}/staff` (201). `relation` is
 * always `'staff'` and `can_delegate` always `false` — a delegated volunteer
 * can never re-delegate.
 */
export interface DelegatedStaff {
  id: number;
  email: string;
  role: DelegatedRole;
  relation: 'staff';
  can_delegate: false;
  /** How many scope rows the grant wrote. The server sends a count, not a list. */
  scopes_granted: number;
  candidate_id: number;
  /**
   * True when the email already belonged to an in-tenant account and this
   * grant ATTACHED it to a second campaign rather than creating anyone. There
   * is no password or invite to hand over on that path — the person already
   * signs in — so the success copy has to say something different.
   */
  attached_existing: boolean;
  /** Present only when the server minted an invite (no password was sent). */
  invite_token?: string | null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * List the candidates the caller is linked to, with reach + delegation flag.
 * `signal` lets HomeView's badge fetch abort on unmount, matching the other
 * play-count wrappers.
 */
export async function listMyCandidates(
  signal?: AbortSignal,
): Promise<MyCandidate[]> {
  const res = await client.get<MyCandidate[]>('/candidates/mine', { signal });
  return res.data;
}

/**
 * One person on a candidate's roster (`GET /candidates/{id}/staff`).
 *
 * The field-vs-committee grouping the UI draws is derived from `role`, not
 * stored: `canvasser`/`viewer` are the only roles delegation can mint, so
 * they render as "field volunteers" and everything else as committee staff.
 * The server deliberately makes no provenance claim (nothing records who
 * created a link), and neither should this type.
 */
export interface CandidateStaffMember {
  user_id: number;
  email: string;
  full_name: string | null;
  role: string;
  relation: CandidateRelation;
  can_delegate: boolean;
  is_active: boolean;
  scope_count: number;
  /** A live, unredeemed invite exists — they haven't picked up the account. */
  invite_pending: boolean;
}

export interface CandidateStaffResponse {
  candidate_id: number;
  slug: string;
  full_name: string;
  members: CandidateStaffMember[];
}

/**
 * The roster read that pairs with `delegateStaff`. A candidate-linked caller
 * gets only their own race (403 on a peer's id); coordinators/admins any
 * in-tenant candidate.
 */
export async function listCandidateStaff(
  candidateId: number,
  signal?: AbortSignal,
): Promise<CandidateStaffResponse> {
  const res = await client.get<CandidateStaffResponse>(
    `/candidates/${candidateId}/staff`,
    { signal },
  );
  return res.data;
}

/**
 * Provision a scoped volunteer under a candidate. Lets axios errors propagate
 * so the caller can surface `err.response?.data?.detail` (403 / 422 / 409).
 */
export async function delegateStaff(
  candidateId: number,
  payload: DelegateStaffPayload,
): Promise<DelegatedStaff> {
  const res = await client.post<DelegatedStaff>(
    `/candidates/${candidateId}/staff`,
    payload,
  );
  return res.data;
}
